import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api";
import { PageHeader } from "../components/PageHeader";
import { EmptyState } from "../components/EmptyState";
import { RecipeImporter } from "../components/RecipeImporter";
import { CatalogColumnPicker } from "../components/CatalogColumnPicker";
import { useCatalogColumns } from "../lib/catalogColumns";
import { ALL_MEAL_TYPES, MEAL_TYPE_LABELS } from "../lib/utils";
import { formatRecipeLastUsed, sortRecipes, type RecipeSortBy } from "../lib/recipeSort";
import type { Recipe } from "../types";

type GroupBy = "none" | "mealType" | "servings";

const RECIPE_COLUMNS = [
  { id: "name" as const, label: "Název", defaultVisible: true, locked: true },
  { id: "mealTypes" as const, label: "Vhodné pro", defaultVisible: true },
  { id: "servings" as const, label: "Porce", defaultVisible: true },
  { id: "lastUsed" as const, label: "Naposledy v jídelníčku", defaultVisible: false },
  { id: "steps" as const, label: "Počet kroků postupu", defaultVisible: false },
  { id: "actions" as const, label: "Akce", defaultVisible: true, locked: true },
];

interface RecipeGroup {
  key: string;
  label: string;
  recipes: Recipe[];
}

export default function RecipesPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { data } = useQuery({
    queryKey: ["recipes"],
    queryFn: () => api.get<{ recipes: Recipe[] }>("/api/recipes"),
  });

  const [name, setName] = useState("");
  const [filter, setFilter] = useState("");
  const [groupBy, setGroupBy] = useState<GroupBy>("none");
  const [sortBy, setSortBy] = useState<RecipeSortBy>("name");
  const [importerOpen, setImporterOpen] = useState(false);

  const { visible, visibleColumns, toggle, isVisible } = useCatalogColumns(
    "jedalnicek:columns:recipes",
    RECIPE_COLUMNS
  );

  const create = useMutation({
    mutationFn: (n: string) => api.post<{ id: string }>("/api/recipes", { name: n }),
    onSuccess: (res) => {
      setName("");
      qc.invalidateQueries({ queryKey: ["recipes"] });
      navigate(`/recepty/${res.id}`);
    },
  });
  const del = useMutation({
    mutationFn: (id: string) => api.delete(`/api/recipes/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["recipes"] }),
  });

  const list = data?.recipes ?? [];

  const filtered = useMemo(() => {
    let rows = list;
    if (filter.trim()) {
      const f = filter.trim().toLowerCase();
      rows = rows.filter((r) => r.name.toLowerCase().includes(f));
    }
    return sortRecipes(rows, sortBy);
  }, [list, filter, sortBy]);

  const groups = useMemo<RecipeGroup[]>(() => groupRecipes(filtered, groupBy, sortBy), [filtered, groupBy, sortBy]);

  return (
    <div>
      <PageHeader
        icon="🍳"
        title="Recepty"
        description="Katalog receptů a jejich surovin."
        actions={
          <button
            type="button"
            className="btn-secondary flex items-center gap-1"
            onClick={() => setImporterOpen(true)}
          >
            <span aria-hidden>📥</span>
            <span>Importer</span>
          </button>
        }
      />

      <RecipeImporter open={importerOpen} onClose={() => setImporterOpen(false)} />

      <div className="card card-body mb-6">
        <h2 className="font-semibold mb-3 flex items-center gap-2">
          <span aria-hidden>📝</span>
          <span>Nový recept</span>
        </h2>
        <div className="flex flex-col sm:flex-row gap-2">
          <input
            className="input"
            placeholder="např. Špagety bolognese"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
          <button
            className="btn-primary flex items-center gap-1"
            disabled={!name.trim() || create.isPending}
            onClick={() => create.mutate(name.trim())}
          >
            <span aria-hidden>➕</span>
            <span>Vytvořit</span>
          </button>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row gap-2 mb-4 sm:items-center">
        <input
          className="input sm:max-w-xs"
          placeholder="🔍 Hledat recept…"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
        />
        <div className="flex flex-wrap items-center gap-2 sm:ml-auto">
          <label className="text-sm text-slate-600 whitespace-nowrap flex items-center gap-2">
            <span>Řadit:</span>
            <select
              className="input sm:max-w-[200px]"
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as RecipeSortBy)}
            >
              <option value="name">Podle názvu</option>
              <option value="lastUsedAsc">Nejdéle nepoužité</option>
              <option value="lastUsedDesc">Naposledy použité</option>
            </select>
          </label>
          <label className="text-sm text-slate-600 whitespace-nowrap flex items-center gap-2">
            <span>Seskupit:</span>
            <select
              className="input sm:max-w-[200px]"
              value={groupBy}
              onChange={(e) => setGroupBy(e.target.value as GroupBy)}
            >
              <option value="none">— bez seskupení —</option>
              <option value="mealType">Vhodné pro (chod)</option>
              <option value="servings">Počet porcí</option>
            </select>
          </label>
          <CatalogColumnPicker columns={RECIPE_COLUMNS} visible={visible} onToggle={toggle} />
        </div>
      </div>

      {list.length === 0 ? (
        <EmptyState
          icon="🍳"
          title="Zatím nemáte žádné recepty"
          description="Vytvořte první recept a přidejte jeho suroviny s množstvím."
        />
      ) : filtered.length === 0 ? (
        <EmptyState
          icon="🔍"
          title="Nic nenalezeno"
          description={`Žádný recept neodpovídá hledanému výrazu „${filter}".`}
        />
      ) : (
        <div className="card overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left">
              <tr>
                {visibleColumns.map((col) => (
                  <th
                    key={col.id}
                    className={`px-4 py-2 ${col.id === "servings" || col.id === "steps" ? "text-center w-24" : ""} ${col.id === "lastUsed" ? "whitespace-nowrap" : ""} ${col.id === "actions" ? "text-right w-24" : ""}`}
                  >
                    {col.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y">
              {groups.map((g) => (
                <RecipeGroupRows
                  key={g.key}
                  group={g}
                  showHeader={groupBy !== "none"}
                  totalCols={visibleColumns.length}
                  isVisible={isVisible}
                  onDelete={(r) => {
                    if (confirm(`Smazat recept "${r.name}"?`)) del.mutate(r.id);
                  }}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function RecipeGroupRows({
  group,
  showHeader,
  totalCols,
  isVisible,
  onDelete,
}: {
  group: RecipeGroup;
  showHeader: boolean;
  totalCols: number;
  isVisible: (id: (typeof RECIPE_COLUMNS)[number]["id"]) => boolean;
  onDelete: (r: Recipe) => void;
}) {
  return (
    <>
      {showHeader && (
        <tr className="bg-slate-50/60">
          <td colSpan={totalCols} className="px-4 py-2 text-xs font-semibold uppercase tracking-wide text-slate-600">
            {group.label} <span className="text-slate-400 normal-case">({group.recipes.length})</span>
          </td>
        </tr>
      )}
      {group.recipes.map((r) => (
        <tr key={`${group.key}-${r.id}`}>
          {isVisible("name") && (
            <td className="px-4 py-2">
              <Link
                to={`/recepty/${r.id}`}
                className="font-medium text-brand-700 hover:underline flex items-center gap-1"
              >
                <span aria-hidden>📄</span>
                <span>{r.name}</span>
              </Link>
            </td>
          )}
          {isVisible("mealTypes") && (
            <td className="px-4 py-2">
              <div className="flex flex-wrap gap-1">
                {(r.mealTypes ?? []).length === 0 ? (
                  <span className="text-[10px] uppercase tracking-wide rounded-full bg-slate-100 text-slate-500 px-2 py-0.5">
                    univerzální
                  </span>
                ) : (
                  (r.mealTypes ?? []).map((mt) => (
                    <span
                      key={mt}
                      className="text-[10px] uppercase tracking-wide rounded-full bg-brand-50 text-brand-700 px-2 py-0.5"
                    >
                      {MEAL_TYPE_LABELS[mt] ?? mt}
                    </span>
                  ))
                )}
              </div>
            </td>
          )}
          {isVisible("servings") && (
            <td className="px-4 py-2 text-center text-slate-700">{r.servings}</td>
          )}
          {isVisible("lastUsed") && (
            <td className="px-4 py-2 text-slate-600 whitespace-nowrap">
              {formatRecipeLastUsed(r.lastUsedInMealPlanAt)}
            </td>
          )}
          {isVisible("steps") && (
            <td className="px-4 py-2 text-center text-slate-700">
              {(r.instructions ?? []).length === 0 ? (
                <span className="text-slate-400">—</span>
              ) : (
                r.instructions.length
              )}
            </td>
          )}
          {isVisible("actions") && (
            <td className="px-4 py-2 text-right whitespace-nowrap">
              <Link
                to={`/recepty/${r.id}`}
                className="btn-ghost text-xs inline-flex items-center gap-1"
                title="Otevřít recept"
              >
                <span aria-hidden>↗</span>
                <span>Otevřít</span>
              </Link>
              <button
                className="btn-ghost text-xs text-red-600 inline-flex items-center gap-1"
                title="Smazat recept"
                onClick={() => onDelete(r)}
              >
                <span aria-hidden>🗑️</span>
                <span>Smazat</span>
              </button>
            </td>
          )}
        </tr>
      ))}
    </>
  );
}

/**
 * Vrátí recepty rozdělené do skupin podle zvoleného atributu.
 * - "none": jedna anonymní skupina se všemi recepty.
 * - "mealType": recept patří do KAŽDÉ skupiny chodu, který má v `mealTypes`.
 *   Univerzální recept (prázdné pole) jde do speciální skupiny "Univerzální".
 *   Pořadí skupin: snídaně → svačina → oběd → odp. svačina → večeře → univerzální.
 * - "servings": jedna skupina pro každou unikátní hodnotu porcí (vzestupně).
 */
function groupRecipes(recipes: Recipe[], groupBy: GroupBy, sortBy: RecipeSortBy): RecipeGroup[] {
  const sort = (rs: Recipe[]) => sortRecipes(rs, sortBy);

  if (groupBy === "none") {
    return [{ key: "all", label: "Vše", recipes: sort([...recipes]) }];
  }

  if (groupBy === "servings") {
    const byServings = new Map<number, Recipe[]>();
    for (const r of recipes) {
      const k = r.servings;
      if (!byServings.has(k)) byServings.set(k, []);
      byServings.get(k)!.push(r);
    }
    return Array.from(byServings.entries())
      .sort(([a], [b]) => a - b)
      .map(([servings, rs]) => ({
        key: `servings-${servings}`,
        label: `${servings} ${czechPorce(servings)}`,
        recipes: sort(rs),
      }));
  }

  // groupBy === "mealType"
  const buckets = new Map<string, Recipe[]>();
  for (const mt of ALL_MEAL_TYPES) buckets.set(mt.id, []);
  buckets.set("_universal", []);

  for (const r of recipes) {
    const mts = r.mealTypes ?? [];
    if (mts.length === 0) {
      buckets.get("_universal")!.push(r);
    } else {
      for (const mt of mts) {
        if (buckets.has(mt)) buckets.get(mt)!.push(r);
      }
    }
  }

  const out: RecipeGroup[] = [];
  for (const mt of ALL_MEAL_TYPES) {
    const rs = buckets.get(mt.id) ?? [];
    if (rs.length > 0) out.push({ key: mt.id, label: mt.label, recipes: sort(rs) });
  }
  const uni = buckets.get("_universal") ?? [];
  if (uni.length > 0) out.push({ key: "_universal", label: "Univerzální", recipes: sort(uni) });
  return out;
}

function czechPorce(n: number): string {
  if (n === 1) return "porce";
  if (n >= 2 && n <= 4) return "porce";
  return "porcí";
}
